import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Lesson, UserProgress } from "../../types/education";
import { useEducationProgress } from "../../hooks/useEducationProgress";
import { LessonTemplateRenderer } from "./LessonTemplateRenderer";
import { getLessonContentSchema } from "../../data/lessons/lessonContentSchemas";
import {
  getLessonContent,
  parseLessonMetadata,
} from "../../data/education/lessonContentLoader";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  BookOpen,
  Star,
  Share2,
  StickyNote,
  Award,
  Target,
  Save,
  MessageSquare,
} from "lucide-react";

interface LessonPlayerProps {
  lesson: Lesson;
  onBack: () => void;
  onComplete: (lessonId: string) => void;
  initialProgress?: UserProgress;
  onProgressUpdate?: (lessonId: string, progress: number) => void;
}

// interface InteractiveElement {
//   id: string;
//   type:
//     | "color-wheel"
//     | "harmony-explorer"
//     | "color-mixer"
//     | "quiz"
//     | "exercise";
//   props?: Record<string, any>;
//   required: boolean;
// }

export const LessonPlayer: React.FC<LessonPlayerProps> = ({
  lesson,
  onBack,
  onComplete,
  initialProgress,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeSpent, setTimeSpent] = useState(0);
  const [completedPages, setCompletedPages] = useState<Set<string>>(new Set());
  const [_completedElements, _setCompletedElements] = useState<Set<string>>(
    new Set(),
  );
  const [showNotes, setShowNotes] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState("");
  const [showShare, setShowShare] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [showDiscussion, setShowDiscussion] = useState(false);
  const [newCommentContent, setNewCommentContent] = useState("");

  // Get note management methods from education progress hook
  const {
    addNote,
    getNotesForLesson,
    deleteNote,
    toggleNoteBookmark,
    updateLessonProgress,
    validateLessonCompletion,
    addLessonRating,
    updateLessonRating,
    getLessonRating,
    addLessonComment,
    getLessonComments,
    deleteLessonComment,
    toggleCommentPin,
  } = useEducationProgress();

  // Get lesson content - try markdown first, then fall back to schema
  const lessonContent = useMemo(() => {
    // Try to get markdown content first
    const markdownContent = getLessonContent(lesson.id);
    if (markdownContent) {
      // Parse markdown content and convert to schema format
      const metadata = parseLessonMetadata(markdownContent);
      // const interactiveElements = extractInteractiveElements(markdownContent);

      // Convert markdown to structured schema (simplified version)
      return {
        version: "1.0.0",
        metadata: {
          title: lesson.title,
          description: lesson.description,
          difficulty: metadata.difficulty || lesson.difficulty,
          estimatedDuration:
            parseInt(metadata.duration || lesson.duration) || 45,
          category: metadata.category || lesson.category,
          tags: [],
        },
        objectives: metadata.objectives || lesson.objectives || [],
        outcomes: lesson.outcomes || [],
        prerequisites: lesson.prerequisites || [],
        pages: [
          {
            id: `${lesson.id}-page-1`,
            title: lesson.title,
            description: lesson.description,
            content: [
              {
                type: "text" as const,
                content: markdownContent.substring(0, 1000) + "...", // First 1000 chars as preview
              },
            ],
            requiredForCompletion: true,
            objectives: metadata.objectives || lesson.objectives || [],
          },
        ],
      } as import("./LessonTemplateRenderer").LessonContentSchema;
    }

    // Fall back to structured schema
    return getLessonContentSchema(lesson.id);
  }, [lesson.id]);

  // Get existing lesson rating
  const existingRating = useMemo(() => {
    return getLessonRating(lesson.id);
  }, [lesson.id, getLessonRating]);

  // Get lesson comments from persistent storage
  const lessonComments = useMemo(() => {
    return getLessonComments(lesson.id);
  }, [lesson.id, getLessonComments]);

  // Get lesson notes from persistent storage
  const lessonNotes = useMemo(() => {
    return getNotesForLesson(lesson.id);
  }, [lesson.id, getNotesForLesson]);

  // Real-time completion validation
  const completionValidation = useMemo(() => {
    if (!lessonContent) return null;

    // Create completion criteria from lesson content
    const criteria = {
      requiredPages: lessonContent.pages
        .filter((page) => page.requiredForCompletion)
        .map((page) => page.id),
      minimumTimeSpent: lessonContent.metadata.estimatedDuration * 60, // Convert minutes to seconds
      minimumQuizScore: 80, // Default minimum quiz score
      requiredInteractiveElements: [], // Would be populated from lesson content
    };

    return validateLessonCompletion(
      criteria,
      completedPages,
      _completedElements,
      timeSpent,
      [], // Quiz scores would be tracked separately
    );
  }, [
    lesson.id,
    lessonContent,
    completedPages,
    _completedElements,
    timeSpent,
    validateLessonCompletion,
  ]);

  const canComplete = completionValidation?.canComplete || false;

  const currentProgress = useMemo(() => {
    if (!lessonContent) return 0;
    const totalPages = lessonContent.pages.length;
    const completedCount = completedPages.size;
    return totalPages > 0 ? Math.round((completedCount / totalPages) * 100) : 0;
  }, [lessonContent, completedPages.size]);

  // Initialize completed pages from initial progress
  useEffect(() => {
    if (initialProgress?.completed) {
      // If lesson was previously completed, mark all pages as complete
      if (lessonContent) {
        const allPageIds = new Set(lessonContent.pages.map((p) => p.id));
        setCompletedPages(allPageIds);
      }
    }
  }, [initialProgress, lessonContent]);

  // Timer for tracking time spent
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPlaying) {
      interval = setInterval(() => {
        setTimeSpent((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  // Auto-start lesson when loaded
  useEffect(() => {
    setIsPlaying(true);
  }, []);

  // Auto-save progress every 30 seconds during active lesson
  useEffect(() => {
    if (!isPlaying) return;

    const autoSaveInterval = setInterval(async () => {
      setIsSaving(true);
      try {
        await updateLessonProgress(lesson.id, {
          progress: currentProgress,
          timeSpent,
          completedPages: Array.from(completedPages),
        });
        setLastSaved(new Date());
      } catch (error) {
        console.error("Auto-save failed:", error);
      } finally {
        setIsSaving(false);
      }
    }, 30000); // Save every 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [
    isPlaying,
    lesson.id,
    currentProgress,
    timeSpent,
    completedPages,
    updateLessonProgress,
  ]);

  // Session recovery - restore progress from initialProgress
  useEffect(() => {
    if (initialProgress && lessonContent) {
      // Restore completed pages from previous session
      if (initialProgress.completedPages) {
        setCompletedPages(new Set(initialProgress.completedPages));
      }

      // Restore time spent from previous session
      if (initialProgress.timeSpent) {
        setTimeSpent(initialProgress.timeSpent);
      }
    }
  }, [initialProgress, lessonContent]);

  // Save progress on page completion with visual feedback
  useEffect(() => {
    if (completedPages.size > 0) {
      setIsSaving(true);
      updateLessonProgress(lesson.id, {
        progress: currentProgress,
        timeSpent,
        completedPages: Array.from(completedPages),
      })
        .then(() => setLastSaved(new Date()))
        .catch((error) => {
          console.error("Failed to save page completion progress:", error);
        })
        .finally(() => setIsSaving(false));
    }
  }, [
    completedPages,
    lesson.id,
    currentProgress,
    timeSpent,
    updateLessonProgress,
  ]);

  const handlePageComplete = useCallback((pageId: string) => {
    setCompletedPages((prev) => new Set([...prev, pageId]));
  }, []);

  const handleInteractiveComplete = useCallback(
    (_elementId: string, _result: any) => {
      // Element completion logic would be implemented here
    },
    [],
  );

  const handleCompleteLesson = useCallback(async () => {
    // Show rating prompt before completing lesson
    if (canComplete && !existingRating) {
      setShowRating(true);
      return;
    }

    // If already rated or user skips rating, complete the lesson
    await updateLessonProgress(lesson.id, { completed: true, progress: 100 });
    onComplete(lesson.id);
  }, [
    lesson.id,
    canComplete,
    existingRating,
    updateLessonProgress,
    onComplete,
  ]);

  const handleSubmitRating = useCallback(async () => {
    if (rating > 0) {
      try {
        if (existingRating) {
          await updateLessonRating(lesson.id, rating, review);
        } else {
          await addLessonRating(lesson.id, rating, review);
        }

        // Complete the lesson after rating
        await updateLessonProgress(lesson.id, {
          completed: true,
          progress: 100,
        });
        onComplete(lesson.id);
      } catch (error) {
        console.error("Failed to save rating:", error);
      }
    }
  }, [
    lesson.id,
    rating,
    review,
    existingRating,
    addLessonRating,
    updateLessonRating,
    updateLessonProgress,
    onComplete,
  ]);

  const handleSkipRating = useCallback(async () => {
    // Complete the lesson without rating
    await updateLessonProgress(lesson.id, { completed: true, progress: 100 });
    onComplete(lesson.id);
  }, [lesson.id, updateLessonProgress, onComplete]);

  const handleAddComment = useCallback(async () => {
    if (newCommentContent.trim()) {
      try {
        await addLessonComment(lesson.id, newCommentContent.trim());
        setNewCommentContent("");
      } catch (error) {
        console.error("Failed to add comment:", error);
      }
    }
  }, [lesson.id, newCommentContent, addLessonComment]);

  const handleDeleteComment = useCallback(
    async (commentId: string) => {
      try {
        await deleteLessonComment(commentId);
      } catch (error) {
        console.error("Failed to delete comment:", error);
      }
    },
    [deleteLessonComment],
  );

  const handleToggleCommentPin = useCallback(
    async (commentId: string) => {
      try {
        await toggleCommentPin(commentId);
      } catch (error) {
        console.error("Failed to toggle comment pin:", error);
      }
    },
    [toggleCommentPin],
  );

  const handleAddNote = useCallback(async () => {
    if (newNoteContent.trim()) {
      try {
        await addNote(lesson.id, newNoteContent.trim());
        setNewNoteContent("");
      } catch (error) {
        console.error("Failed to add note:", error);
      }
    }
  }, [lesson.id, newNoteContent, addNote]);

  const handleDeleteNote = useCallback(
    async (noteId: string) => {
      try {
        await deleteNote(noteId);
      } catch (error) {
        console.error("Failed to delete note:", error);
      }
    },
    [deleteNote],
  );

  const handleToggleNoteBookmark = useCallback(
    async (noteId: string) => {
      try {
        await toggleNoteBookmark(noteId);
      } catch (error) {
        console.error("Failed to toggle note bookmark:", error);
      }
    },
    [toggleNoteBookmark],
  );

  const handleShareLesson = useCallback(() => {
    // Generate share URL with lesson ID
    const baseUrl = window.location.origin;
    const url = `${baseUrl}/lessons/${lesson.id}`;
    setShareUrl(url);
    setShowShare(true);
  }, [lesson.id]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy link:", error);
    }
  }, [shareUrl]);

  const handleSocialShare = useCallback(
    (platform: "twitter" | "linkedin" | "facebook") => {
      const encodedUrl = encodeURIComponent(shareUrl);
      const encodedTitle = encodeURIComponent(
        `Check out this lesson: ${lesson.title}`,
      );

      let socialUrl = "";
      switch (platform) {
        case "twitter":
          socialUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
          break;
        case "linkedin":
          socialUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
          break;
        case "facebook":
          socialUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
          break;
      }

      if (socialUrl) {
        window.open(socialUrl, "_blank", "width=600,height=400");
      }
    },
    [shareUrl, lesson.title],
  );

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }, []);

  if (!lessonContent) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium">
                Lesson Content Not Available
              </h3>
              <p className="text-muted-foreground">
                The content for "{lesson.title}" is not yet available.
              </p>
              <Button variant="outline" onClick={onBack} className="mt-4">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Catalog
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Lesson Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <Button variant="outline" size="sm" onClick={onBack}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Catalog
                </Button>
                <Badge variant="secondary">
                  {lessonContent.metadata.category}
                </Badge>
                <Badge variant="outline">
                  {lessonContent.metadata.difficulty}
                </Badge>
              </div>
              <CardTitle className="text-2xl">
                {lessonContent.metadata.title}
              </CardTitle>
              <CardDescription className="mt-2">
                {lessonContent.metadata.description}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleShareLesson}>
                <Share2 className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowNotes(!showNotes)}
              >
                <StickyNote className="h-4 w-4" />
                {lessonNotes.length > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs">
                    {lessonNotes.length}
                  </Badge>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDiscussion(!showDiscussion)}
              >
                <MessageSquare className="h-4 w-4" />
                {lessonComments.length > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs">
                    {lessonComments.length}
                  </Badge>
                )}
              </Button>
              {/* Auto-save indicator */}
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                {isSaving ? (
                  <div className="flex items-center gap-1 text-blue-600">
                    <Save className="h-3 w-3 animate-spin" />
                    <span>Saving...</span>
                  </div>
                ) : lastSaved ? (
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="h-3 w-3" />
                    <span>Saved</span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>{formatTime(timeSpent)}</span>
            </div>
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-muted-foreground" />
              <span>{lessonContent.pages.length} pages</span>
            </div>
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-muted-foreground" />
              <span>{currentProgress}% Complete</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="h-4 w-4 text-muted-foreground" />
              <span>{completedPages.size} pages done</span>
            </div>
          </div>
          <div className="mt-4">
            <Progress value={currentProgress} className="w-full" />
          </div>
        </CardContent>
      </Card>

      {/* Main Content Area */}
      <div className="grid lg:grid-cols-4 gap-6">
        {/* Lesson Content */}
        <div className="lg:col-span-3">
          <LessonTemplateRenderer
            lessonContent={lessonContent}
            onPageComplete={handlePageComplete}
            onInteractiveComplete={handleInteractiveComplete}
            onLessonComplete={handleCompleteLesson}
            completedPages={completedPages}
            completedElements={_completedElements}
          />

          {/* Rating Modal */}
          {showRating && (
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Star className="h-5 w-5 text-blue-600" />
                  Rate Your Experience
                </CardTitle>
                <CardDescription>
                  How would you rate "{lesson.title}"? Your feedback helps us
                  improve the learning experience.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Star Rating */}
                  <div className="flex justify-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setRating(star)}
                        className="p-2 transition-colors hover:bg-blue-100 rounded-lg"
                      >
                        <Star
                          className={`w-8 h-8 ${
                            star <= rating
                              ? "fill-yellow-400 text-yellow-400"
                              : "text-gray-300"
                          }`}
                        />
                      </button>
                    ))}
                  </div>

                  {/* Rating Labels */}
                  <div className="flex justify-between text-xs text-gray-600 px-4">
                    <span>Poor</span>
                    <span>Fair</span>
                    <span>Good</span>
                    <span>Very Good</span>
                    <span>Excellent</span>
                  </div>

                  {/* Review Textarea */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Share your thoughts (optional)
                    </label>
                    <textarea
                      className="w-full p-3 border rounded-lg text-sm"
                      rows={4}
                      placeholder="What did you like about this lesson? What could be improved?"
                      value={review}
                      onChange={(e) => setReview(e.target.value)}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      onClick={handleSubmitRating}
                      disabled={rating === 0}
                      className="flex-1"
                    >
                      Submit & Complete
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleSkipRating}
                      className="flex-1"
                    >
                      Skip Rating
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Share Modal */}
          {showShare && (
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Share2 className="h-5 w-5 text-blue-600" />
                  Share Lesson
                </CardTitle>
                <CardDescription>
                  Share "{lesson.title}" with others through your preferred
                  platform.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* URL Copy Section */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Share Link
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={shareUrl}
                        readOnly
                        className="flex-1 p-2 border rounded-lg text-sm bg-white"
                      />
                      <Button
                        onClick={handleCopyLink}
                        variant="outline"
                        size="sm"
                        className="whitespace-nowrap"
                      >
                        {copied ? "Copied!" : "Copy"}
                      </Button>
                    </div>
                  </div>

                  {/* Social Media Sharing */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Share on Social Media
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <Button
                        onClick={() => handleSocialShare("twitter")}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        <div className="w-4 h-4 bg-blue-400 rounded-full"></div>
                        Twitter
                      </Button>
                      <Button
                        onClick={() => handleSocialShare("linkedin")}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        <div className="w-4 h-4 bg-blue-700 rounded-full"></div>
                        LinkedIn
                      </Button>
                      <Button
                        onClick={() => handleSocialShare("facebook")}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        <div className="w-4 h-4 bg-blue-600 rounded-full"></div>
                        Facebook
                      </Button>
                    </div>
                  </div>

                  {/* Additional Options */}
                  <div className="pt-2 border-t">
                    <p className="text-xs text-gray-600 mb-3">
                      Share this lesson to help others learn about{" "}
                      {lesson.category || "this topic"}.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setShowShare(false)}
                      className="w-full"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Lesson Completion */}
          {canComplete && (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">Lesson Complete!</h3>
                  <p className="text-muted-foreground mb-4">
                    Congratulations on completing this lesson.
                  </p>
                  <Button
                    onClick={handleCompleteLesson}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Mark as Completed
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Discussion Panel */}
          {showDiscussion && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Discussion ({lessonComments.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Add new comment */}
                  <div className="space-y-2">
                    <textarea
                      className="w-full p-2 border rounded-md text-sm"
                      rows={3}
                      placeholder="Join the discussion..."
                      value={newCommentContent}
                      onChange={(e) => setNewCommentContent(e.target.value)}
                      onKeyPress={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter" && e.ctrlKey) {
                          e.preventDefault();
                          handleAddComment();
                        }
                      }}
                    />
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">
                        Press Ctrl+Enter to post
                      </span>
                      <Button
                        size="sm"
                        onClick={handleAddComment}
                        disabled={!newCommentContent.trim()}
                      >
                        Post Comment
                      </Button>
                    </div>
                  </div>

                  {/* Display existing comments */}
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {lessonComments.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No comments yet. Start the discussion!
                      </p>
                    ) : (
                      lessonComments.map((comment) => (
                        <div
                          key={comment.id}
                          className="text-sm p-3 bg-muted rounded-lg"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">
                                {new Date(
                                  comment.timestamp,
                                ).toLocaleDateString()}
                              </span>
                              {comment.isPinned && (
                                <div
                                  className="w-3 h-3 bg-red-500 rounded-full"
                                  title="Pinned comment"
                                ></div>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleToggleCommentPin(comment.id)
                                }
                                className="h-6 w-6 p-0"
                                title={
                                  comment.isPinned
                                    ? "Unpin comment"
                                    : "Pin comment"
                                }
                              >
                                <div
                                  className={`w-3 h-3 rounded-full ${comment.isPinned ? "bg-red-500" : "bg-gray-300"}`}
                                ></div>
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteComment(comment.id)}
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                title="Delete comment"
                              >
                                ×
                              </Button>
                            </div>
                          </div>
                          <p className="text-gray-700 whitespace-pre-wrap">
                            {comment.content}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes Panel */}
          {showNotes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <StickyNote className="h-5 w-5" />
                  Notes ({lessonNotes.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Add new note */}
                  <div className="space-y-2">
                    <textarea
                      className="w-full p-2 border rounded-md text-sm"
                      rows={3}
                      placeholder="Add a note about this lesson..."
                      value={newNoteContent}
                      onChange={(e) => setNewNoteContent(e.target.value)}
                      onKeyPress={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter" && e.ctrlKey) {
                          e.preventDefault();
                          handleAddNote();
                        }
                      }}
                    />
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">
                        Press Ctrl+Enter to save
                      </span>
                      <Button
                        size="sm"
                        onClick={handleAddNote}
                        disabled={!newNoteContent.trim()}
                      >
                        Add Note
                      </Button>
                    </div>
                  </div>

                  {/* Display existing notes */}
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {lessonNotes.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No notes yet. Start taking notes to track your learning!
                      </p>
                    ) : (
                      lessonNotes.map((note) => (
                        <div
                          key={note.id}
                          className="text-sm p-3 bg-muted rounded-lg"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">
                                {new Date(note.timestamp).toLocaleDateString()}
                              </span>
                              {note.isBookmarked && (
                                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleToggleNoteBookmark(note.id)
                                }
                                className="h-6 w-6 p-0"
                              >
                                <Star
                                  className={`w-3 h-3 ${note.isBookmarked ? "fill-yellow-400 text-yellow-400" : "text-gray-400"}`}
                                />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteNote(note.id)}
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                              >
                                ×
                              </Button>
                            </div>
                          </div>
                          <p className="text-gray-700 whitespace-pre-wrap">
                            {note.content}
                          </p>
                          {note.pageId && (
                            <p className="text-xs text-muted-foreground mt-2">
                              Page: {note.pageId}
                            </p>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Completion Requirements Panel */}
          {completionValidation && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Completion Requirements
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Page Requirements */}
                  <div className="flex items-center justify-between text-sm">
                    <span>Required Pages</span>
                    <span
                      className={`font-medium ${
                        completionValidation.progressDetails.pagesCompleted ===
                        completionValidation.progressDetails.pagesRequired
                          ? "text-green-600"
                          : "text-orange-600"
                      }`}
                    >
                      {completionValidation.progressDetails.pagesCompleted}/
                      {completionValidation.progressDetails.pagesRequired}
                    </span>
                  </div>
                  <Progress
                    value={
                      (completionValidation.progressDetails.pagesCompleted /
                        completionValidation.progressDetails.pagesRequired) *
                      100
                    }
                    className="w-full h-2"
                  />

                  {/* Time Requirements */}
                  {completionValidation.progressDetails.timeRequired && (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span>Time Spent</span>
                        <span
                          className={`font-medium ${
                            completionValidation.progressDetails.timeSpent >=
                            completionValidation.progressDetails.timeRequired
                              ? "text-green-600"
                              : "text-orange-600"
                          }`}
                        >
                          {Math.floor(
                            completionValidation.progressDetails.timeSpent / 60,
                          )}
                          min /{" "}
                          {Math.floor(
                            completionValidation.progressDetails.timeRequired! /
                              60,
                          )}
                          min
                        </span>
                      </div>
                      <Progress
                        value={
                          (completionValidation.progressDetails.timeSpent /
                            completionValidation.progressDetails.timeRequired) *
                          100
                        }
                        className="w-full h-2"
                      />
                    </>
                  )}

                  {/* Quiz Score Requirements */}
                  {completionValidation.progressDetails.quizScoreRequired && (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span>Quiz Score</span>
                        <span
                          className={`font-medium ${
                            (completionValidation.progressDetails.quizScore ||
                              0) >=
                            completionValidation.progressDetails
                              .quizScoreRequired!
                              ? "text-green-600"
                              : "text-orange-600"
                          }`}
                        >
                          {completionValidation.progressDetails.quizScore || 0}%
                          /{" "}
                          {
                            completionValidation.progressDetails
                              .quizScoreRequired
                          }
                          %
                        </span>
                      </div>
                      <Progress
                        value={
                          ((completionValidation.progressDetails.quizScore ||
                            0) /
                            completionValidation.progressDetails
                              .quizScoreRequired!) *
                          100
                        }
                        className="w-full h-2"
                      />
                    </>
                  )}

                  {/* Interactive Elements Requirements */}
                  {completionValidation.progressDetails.elementsRequired >
                    0 && (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span>Interactive Exercises</span>
                        <span
                          className={`font-medium ${
                            completionValidation.progressDetails
                              .elementsCompleted ===
                            completionValidation.progressDetails
                              .elementsRequired
                              ? "text-green-600"
                              : "text-orange-600"
                          }`}
                        >
                          {
                            completionValidation.progressDetails
                              .elementsCompleted
                          }
                          /
                          {
                            completionValidation.progressDetails
                              .elementsRequired
                          }
                        </span>
                      </div>
                      <Progress
                        value={
                          (completionValidation.progressDetails
                            .elementsCompleted /
                            completionValidation.progressDetails
                              .elementsRequired) *
                          100
                        }
                        className="w-full h-2"
                      />
                    </>
                  )}

                  {/* Missing Requirements */}
                  {completionValidation.missingRequirements.length > 0 && (
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="text-sm font-medium text-orange-800 mb-2">
                        Still needed:
                      </p>
                      <ul className="text-xs text-orange-700 space-y-1">
                        {completionValidation.missingRequirements.map(
                          (requirement, index) => (
                            <li key={index}>• {requirement}</li>
                          ),
                        )}
                      </ul>
                    </div>
                  )}

                  {/* Completion Status */}
                  {completionValidation.canComplete && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-800 font-medium">
                        ✓ All requirements met! You can complete this lesson.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Page Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Page Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {lessonContent.pages.map((page, _index) => (
                  <div
                    key={page.id}
                    className={`flex items-center justify-between p-2 rounded text-sm ${
                      completedPages.has(page.id) ? "bg-green-50" : ""
                    }`}
                  >
                    <span className="truncate">{page.title}</span>
                    {completedPages.has(page.id) && (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Lesson Rating */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Rate This Lesson</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRating(star)}
                    className="p-1"
                  >
                    <Star
                      className={`h-5 w-5 ${
                        star <= rating
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-gray-300"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
